import React, { useEffect, useState, useCallback } from 'react';
import {
  clientsApi, Client, ClientBuilding, ClientBuildingSearchHit, ClientCategory, ClientPriceMode,
} from '../services/api';
import { Button } from '../ds/components/Button';
import { Icon } from '../ds/components/Icon';
import { InputField } from '../ds/InputField';
import { ModalShell } from '../ds/ModalShell';
import { ConfirmModal } from '../ds/ConfirmModal';
import HeatSourceSummaryModal from './HeatSourceSummaryModal';
import ClientCategoriesModal from './ClientCategoriesModal';
import { useAuthOptional } from '../contexts/AuthContext';
import { nipError as checkNip, emailError as checkEmail, normalizeNip } from '../utils/clientFields';

type FormState = {
  name: string;
  shortName: string;
  nip: string;
  address: string;
  phone: string;
  email: string;
  inspectionRate: string;
  ceebRate: string;
  // 068 (C3/C4): kategoria ('' = bez kategorii) + pola wewnętrzne (nie trafiają do dokumentów).
  categoryId: string;
  internalNote: string;
  priceMode: '' | ClientPriceMode;
  priceFlat: string;
};

const emptyForm: FormState = {
  name: '', shortName: '', nip: '', address: '', phone: '', email: '',
  inspectionRate: '0', ceebRate: '0',
  categoryId: '', internalNote: '', priceMode: '', priceFlat: '',
};

/** Filtr kategorii w zakładce: '' = wszystkie, 'none' = bez kategorii, inaczej id kategorii. */
export const CATEGORY_FILTER_NONE = 'none';

const PRICE_MODE_LABELS: Record<ClientPriceMode, string> = {
  per_apartment: 'za lokal',
  flat: 'za całość',
};

const zl = (n: number) => `${n.toFixed(2).replace('.', ',')} zł`;

/** Krótki opis ceny do tabeli (pole wewnętrzne): „za lokal 120,00 zł" / „za całość 350,00 zł". */
export function priceSummary(c: Pick<Client, 'priceMode' | 'priceFlat' | 'inspectionRate'>): string | null {
  if (c.priceMode === 'flat') return `${PRICE_MODE_LABELS.flat}${c.priceFlat != null ? ` ${zl(Number(c.priceFlat))}` : ''}`;
  if (c.priceMode === 'per_apartment') return `${PRICE_MODE_LABELS.per_apartment} ${zl(Number(c.inspectionRate ?? 0))}`;
  return null;
}

const selectStyle: React.CSSProperties = {
  width: '100%', minHeight: 'var(--touch-min)', padding: '0 var(--space-4)',
  boxSizing: 'border-box', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-body)',
  color: 'var(--text-primary)', background: 'var(--surface-card)',
  border: 'var(--border-width-strong) solid var(--border-strong)',
  borderRadius: 'var(--radius-md)',
};
const selectLabelStyle: React.CSSProperties = {
  fontFamily: 'var(--font-sans)', fontSize: 'var(--text-label)',
  fontWeight: 'var(--weight-semibold)' as any, color: 'var(--text-secondary)',
};

// DS-styled cell tokens reused across the table.
const headCellBase: React.CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--text-label)',
  fontWeight: 'var(--weight-semibold)',
  color: 'var(--text-heading)',
  background: 'var(--surface-fill)',
  borderBottom: 'var(--border-width) solid var(--border)',
  padding: 'var(--space-3) var(--space-4)',
  textAlign: 'left',
  whiteSpace: 'nowrap',
};

const headCellSx: React.CSSProperties = headCellBase;
const headCellRight: React.CSSProperties = { ...headCellBase, textAlign: 'right' };
const headCellCenter: React.CSSProperties = { ...headCellBase, textAlign: 'center' };

const bodyCellBase: React.CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--text-caption)',
  color: 'var(--text-primary)',
  borderBottom: 'var(--border-width) solid var(--border)',
  padding: 'var(--space-3) var(--space-4)',
  verticalAlign: 'top',
};

// Kontakt: ogranicz szerokość i pozwól łamać długi e-mail (inaczej rozpycha tabelę → akcje uciekają poza kartę)
const bodyCellContact: React.CSSProperties = { ...bodyCellBase, maxWidth: 240, overflowWrap: 'anywhere' };

const bodyCellSx: React.CSSProperties = bodyCellBase;
const bodyCellRight: React.CSSProperties = { ...bodyCellBase, textAlign: 'right', fontFeatureSettings: '"tnum"' };
const bodyCellCenter: React.CSSProperties = { ...bodyCellBase, textAlign: 'center', verticalAlign: 'middle' };
const bodyCellActions: React.CSSProperties = { ...bodyCellBase, textAlign: 'right', verticalAlign: 'middle', whiteSpace: 'nowrap', width: '1%' };

const foldPl = (v: unknown) => String(v ?? '').toLowerCase()
  .replace(/[ąćęłńóśźż]/g, (ch) => ({ ą: 'a', ć: 'c', ę: 'e', ł: 'l', ń: 'n', ó: 'o', ś: 's', ź: 'z', ż: 'z' } as Record<string, string>)[ch] ?? ch);

/**
 * Filtr listy zarządców: każde słowo zapytania musi wystąpić w nazwie/skrócie/NIP/adresie/e-mailu/
 * telefonie/kategorii/notatce wewnętrznej; `category` ('' = wszystkie, 'none' = bez kategorii, id).
 */
export function filterClients(clients: Client[], query: string, category = ''): Client[] {
  const tokens = foldPl(query).split(/\s+/).filter(Boolean);
  return clients.filter((c) => {
    if (category === CATEGORY_FILTER_NONE && c.categoryId) return false;
    if (category && category !== CATEGORY_FILTER_NONE && c.categoryId !== category) return false;
    if (!tokens.length) return true;
    const blob = foldPl([c.name, c.shortName, c.nip, c.address, c.email, c.phone, c.categoryName, c.internalNote].join(' '));
    return tokens.every((t) => blob.includes(t));
  });
}

const ClientsTab: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [nameError, setNameError] = useState<string | null>(null);
  // Walidacja NIP/e-mail (QA 2026-08-05, S5) — lustro reguł z backend/src/utils/clientFields.js.
  const [nipError, setNipError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<Client | null>(null);
  // Próbka adresów do komunikatu usuwania (spec 2026-08-10) — user ma widzieć, CZEGO
  // dotyczy odpięcie, nie tylko liczbę.
  const [confirmSample, setConfirmSample] = useState<string[]>([]);
  // Modal „Budynki zarządcy": podgląd + hurtowe przepinanie.
  const [buildingsClient, setBuildingsClient] = useState<Client | null>(null);
  // Kontrakt #326: zestawienie źródeł ciepła WSZYSTKICH budynków zarządcy. Otwierane zamiast modala
  // „Budynki zarządcy" (dwa ModalShell naraz = ESC zamyka oba).
  const [hsSummaryClient, setHsSummaryClient] = useState<Client | null>(null);
  const [clientBuildings, setClientBuildings] = useState<ClientBuilding[]>([]);
  const [buildingsLoading, setBuildingsLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [moveTarget, setMoveTarget] = useState('');
  const [moving, setMoving] = useState(false);
  // Hurtowe PRZYPISYWANIE po wyszukiwarce (właściciel 2026-09-15): szukaj adresu → zaznacz → „Przypisz (N)".
  // Trafienia z innym zarządcą są nieaktywne z jego nazwą — przepięcie robi się świadomie z tamtego modalu.
  const [attachQuery, setAttachQuery] = useState('');
  const [attachHits, setAttachHits] = useState<ClientBuildingSearchHit[]>([]);
  const [attachSearching, setAttachSearching] = useState(false);
  const [attachIds, setAttachIds] = useState<Set<string>>(new Set());
  const [attaching, setAttaching] = useState(false);
  // Właściciel 2026-10-02 (docx: Jabłońskiego 2/4 „nie widzę ich w liście" — były przypisane do
  // innego zarządcy, więc trafienia były nieaktywne): cudze trafienia można zaznaczyć, a przepięcie
  // wymaga potwierdzenia w ConfirmModal (lista adresów + obecny zarządca) → attach z reassign:true.
  const [reassignConfirm, setReassignConfirm] = useState<ClientBuildingSearchHit[] | null>(null);
  // Wyszukiwarka zarządców (właściciel 2026-09-15): filtr po nazwie, NIP, adresie, e-mailu, telefonie —
  // po stronie klienta (lista kilkudziesięciu pozycji), bez ogonków i wielkości liter.
  const [clientQuery, setClientQuery] = useState('');
  // 068 (C3): kategorie zarządców (per firma) + filtr zakładki + modal zarządzania listą.
  const [categories, setCategories] = useState<ClientCategory[]>([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [priceFlatError, setPriceFlatError] = useState<string | null>(null);
  const auth = useAuthOptional();
  // Zapis kategorii: admin/superadmin (backend i tak pilnuje). Bez kontekstu logowania (dev) — pokaż.
  const canManageCategories = !auth?.user || auth.user.role === 'admin' || auth.user.role === 'superadmin';

  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      const data = await clientsApi.getAll();
      setClients(data);
    } catch (e: any) {
      setToast({ msg: 'Nie udało się pobrać zarządców', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadClients(); }, [loadClients]);

  const loadCategories = useCallback(async () => {
    try {
      const data = await clientsApi.getCategories();
      setCategories(Array.isArray(data) ? data : []);
    } catch {
      setCategories([]);
      setToast({ msg: 'Nie udało się pobrać kategorii zarządców', severity: 'error' });
    }
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  // Auto-dismiss toast (preserves MUI Snackbar autoHideDuration=3500).
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  // Otwarcie formularza zawsze zaczyna od czystych błędów — inaczej komunikat po nieudanym
  // zapisie zostawał widoczny przy kolejnym otwarciu modala.
  const clearFieldErrors = () => {
    setNameError(null);
    setNipError(null);
    setEmailError(null);
    setPriceFlatError(null);
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    clearFieldErrors();
    setDialogOpen(true);
  };

  const openEdit = (client: Client) => {
    setEditingId(client.id);
    setForm({
      name: client.name,
      shortName: client.shortName || '',
      nip: client.nip || '',
      address: client.address || '',
      phone: client.phone || '',
      email: client.email || '',
      inspectionRate: String(client.inspectionRate ?? 0),
      ceebRate: String(client.ceebRate ?? 0),
      categoryId: client.categoryId || '',
      internalNote: client.internalNote || '',
      priceMode: client.priceMode || '',
      priceFlat: client.priceFlat != null ? String(client.priceFlat) : '',
    });
    clearFieldErrors();
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setNameError('Nazwa zarządcy jest wymagana');
      setToast({ msg: 'Nazwa zarządcy jest wymagana', severity: 'error' });
      return;
    }
    setNameError(null);
    // NIP i e-mail są opcjonalne, ale jeśli coś wpisano — musi mieć sens (S5).
    // Sprawdzamy OBA naraz, żeby nie kazać poprawiać błędów po jednym.
    const nipMsg = checkNip(form.nip);
    const emailMsg = checkEmail(form.email);
    // 068: cena za całość — kwota ≥ 0 (lustro walidatora backendu: kropka albo przecinek, do 2 miejsc).
    // Kwota ma sens tylko przy „jedna cena za całość" — przy innym trybie czyścimy ją ('').
    const flatRaw = form.priceMode === 'flat' ? form.priceFlat.trim() : '';
    const flatMsg = flatRaw && !/^\d+([.,]\d{1,2})?$/.test(flatRaw) ? 'Cena za całość musi być kwotą (np. 1500 albo 1500,50)' : null;
    setNipError(nipMsg);
    setEmailError(emailMsg);
    setPriceFlatError(flatMsg);
    if (nipMsg || emailMsg || flatMsg) {
      setToast({ msg: nipMsg || emailMsg || flatMsg || 'Popraw zaznaczone pola', severity: 'error' });
      return;
    }
    const payload = {
      name: form.name.trim(),
      // 055: '' przy edycji = wyczyszczenie skrótu (backend NULLIF), przy dodawaniu = brak skrótu.
      shortName: form.shortName.trim(),
      // Backend zapisuje NIP znormalizowany — wysyłamy w tej samej postaci, żeby to,
      // co widać po zapisie, zgadzało się z tym, co poszło na serwer.
      nip: normalizeNip(form.nip.trim()) || undefined,
      address: form.address.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      inspectionRate: parseFloat(form.inspectionRate) || 0,
      ceebRate: parseFloat(form.ceebRate) || 0,
      // 068: '' = bez kategorii / wyczyszczenie pola (backend NULLIF), jak shortName.
      categoryId: form.categoryId,
      internalNote: form.internalNote.trim(),
      priceMode: form.priceMode,
      priceFlat: flatRaw ? Number(flatRaw.replace(',', '.')) : ('' as const),
    };
    try {
      setSaving(true);
      if (editingId) {
        await clientsApi.update(editingId, payload);
        setToast({ msg: 'Zmiany zapisane', severity: 'success' });
      } else {
        await clientsApi.create(payload);
        setToast({ msg: 'Zarządca dodany', severity: 'success' });
      }
      setDialogOpen(false);
      await loadClients();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Błąd zapisu', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Otwarcie potwierdzenia usuwania: przy podpiętych budynkach dociągamy próbkę adresów,
  // żeby komunikat mówił KONKRETNIE, co zostanie odpięte (spec 2026-08-10). Błąd pobrania
  // próbki nie blokuje — modal pokaże samą liczbę.
  const openDeleteConfirm = async (client: Client) => {
    setConfirmSample([]);
    setConfirmTarget(client);
    if ((client.buildingsCount ?? 0) > 0) {
      try {
        const rows = await clientsApi.getBuildings(client.id);
        setConfirmSample(rows.slice(0, 5).map((b) => `${b.street} ${b.houseNumber}, ${b.city}`));
      } catch {
        // próbka jest opcjonalna — liczba wystarczy
      }
    }
  };

  const handleDelete = async (client: Client) => {
    const detach = (client.buildingsCount ?? 0) > 0;
    try {
      await clientsApi.delete(client.id, { detach });
      setToast({
        msg: detach
          ? `Zarządca usunięty, odpięto budynki: ${client.buildingsCount}`
          : 'Zarządca usunięty',
        severity: 'success',
      });
      await loadClients();
    } catch (e: any) {
      // 409 = w międzyczasie doszły budynki (nieświeża lista) — powiedz DLACZEGO.
      setToast({
        msg: e?.buildingsCount
          ? `Zarządca ma podpięte budynki (${e.buildingsCount}) — odśwież listę i spróbuj ponownie`
          : (e?.message || 'Nie udało się usunąć zarządcy'),
        severity: 'error',
      });
    }
  };

  const openBuildingsModal = async (client: Client) => {
    setBuildingsClient(client);
    setSelectedIds(new Set());
    setMoveTarget('');
    setAttachQuery('');
    setAttachHits([]);
    setAttachIds(new Set());
    setBuildingsLoading(true);
    try {
      setClientBuildings(await clientsApi.getBuildings(client.id));
    } catch {
      setClientBuildings([]);
      setToast({ msg: 'Nie udało się pobrać budynków zarządcy', severity: 'error' });
    } finally {
      setBuildingsLoading(false);
    }
  };

  // Debounce 300 ms; odpowiedź ze starszego zapytania nie nadpisuje nowszej (guard `alive`).
  useEffect(() => {
    if (!buildingsClient) return;
    const q = attachQuery.trim();
    if (q.length < 2) { setAttachHits([]); setAttachSearching(false); return; }
    let alive = true;
    setAttachSearching(true);
    const t = setTimeout(async () => {
      try {
        const hits = await clientsApi.searchBuildings(q);
        if (alive) setAttachHits(hits);
      } catch {
        if (alive) { setAttachHits([]); setToast({ msg: 'Nie udało się wyszukać budynków', severity: 'error' }); }
      } finally {
        if (alive) setAttachSearching(false);
      }
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [attachQuery, buildingsClient]);

  // Klik „Przypisz (N)": zaznaczone trafienia z INNYM zarządcą → najpierw ConfirmModal.
  const requestAttachBuildings = () => {
    if (!buildingsClient || attachIds.size === 0) return;
    const foreign = attachHits.filter((h) => attachIds.has(h.id) && !!h.clientId && h.clientId !== buildingsClient.id);
    if (foreign.length > 0) { setReassignConfirm(foreign); return; }
    void handleAttachBuildings(false);
  };

  const handleAttachBuildings = async (reassign: boolean) => {
    if (!buildingsClient || attachIds.size === 0) return;
    setAttaching(true);
    try {
      const { attached, reassigned, skipped } = await clientsApi.attachBuildings(buildingsClient.id, {
        buildingIds: Array.from(attachIds),
        ...(reassign ? { reassign: true } : {}),
      });
      const cudze = skipped.filter((x) => x.reason === 'other-client');
      const parts = [`Przypisano budynki (${attached})`];
      if (reassigned) parts.push(`w tym przepięto od innego zarządcy: ${reassigned}`);
      if (cudze.length) parts.push(`pominięto ${cudze.length} z innym zarządcą`);
      setToast({ msg: parts.join('; '), severity: attached > 0 ? 'success' : 'error' });
      setAttachIds(new Set());
      await loadClients();
      setClientBuildings(await clientsApi.getBuildings(buildingsClient.id));
      // odśwież trafienia — przypisane pokażą się jako „już przypisany"
      if (attachQuery.trim().length >= 2) setAttachHits(await clientsApi.searchBuildings(attachQuery.trim()));
    } catch (e: any) {
      setToast({ msg: e?.message || 'Nie udało się przypisać budynków', severity: 'error' });
    } finally {
      setAttaching(false);
    }
  };

  const visibleClients = filterClients(clients, clientQuery, categoryFilter);
  // Kategorie dostępne w formularzu: aktywne z firmy zarządcy + ta, którą zarządca już ma (nawet wyłączona).
  const editingClient = editingId ? clients.find((c) => c.id === editingId) : undefined;
  const formCategories = categories.filter((cat) => {
    if (cat.id === form.categoryId) return true;
    if (!cat.active) return false;
    return !editingClient?.companyId || !cat.companyId || cat.companyId === editingClient.companyId;
  });

  const handleMoveBuildings = async () => {
    if (!buildingsClient || selectedIds.size === 0) return;
    setMoving(true);
    try {
      const { moved } = await clientsApi.moveBuildings(buildingsClient.id, {
        targetClientId: moveTarget || null,
        // Array.from, nie spread — target TS web-admina nie iteruje Set-a (TS2802)
        buildingIds: Array.from(selectedIds),
      });
      const targetName = moveTarget
        ? (clients.find((c) => c.id === moveTarget)?.name || 'wybranego zarządcy')
        : null;
      setToast({
        msg: targetName ? `Przeniesiono budynki (${moved}) do: ${targetName}` : `Odpięto budynki (${moved})`,
        severity: 'success',
      });
      await loadClients();
      // odśwież zawartość modalu (przeniesione znikają z listy źródłowego)
      setSelectedIds(new Set());
      setClientBuildings(await clientsApi.getBuildings(buildingsClient.id));
    } catch (e: any) {
      setToast({ msg: e?.message || 'Nie udało się przenieść budynków', severity: 'error' });
    } finally {
      setMoving(false);
    }
  };

  return (
    <div style={{ maxWidth: '100%', padding: 'var(--space-6)', boxSizing: 'border-box' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 'var(--space-3)',
          marginBottom: 'var(--space-8)',
        }}
      >
        <Icon name="users" size={32} color="var(--action-primary)" />
        <h1
          style={{
            margin: 0,
            flexGrow: 1,
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-h1)',
            fontWeight: 'var(--weight-bold)' as any,
            lineHeight: 'var(--leading-tight)',
            color: 'var(--text-heading)',
          }}
        >
          Zarządcy
        </h1>
        {canManageCategories && (
          <Button variant="secondary" icon="settings" onClick={() => setCategoriesOpen(true)}>Kategorie</Button>
        )}
        <Button variant="primary" icon="plus" onClick={openAdd}>Dodaj zarządcę</Button>
      </div>

      {!loading && clients.length > 0 && (
        <div style={{ marginBottom: 'var(--space-4)', display: 'flex', flexWrap: 'wrap', gap: 'var(--space-4)', alignItems: 'flex-start' }}>
          <div style={{ flex: '1 1 320px', maxWidth: 420 }}>
            <InputField
              label="Szukaj zarządcy"
              placeholder="nazwa, NIP, adres, e-mail, telefon, notatka"
              value={clientQuery}
              onChange={(e) => setClientQuery(e.target.value)}
              hint={clientQuery.trim() || categoryFilter ? `Pokazano ${visibleClients.length} z ${clients.length}` : undefined}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', flex: '0 1 260px' }}>
            <label htmlFor="clients-category-filter" style={selectLabelStyle}>Kategoria</label>
            <select
              id="clients-category-filter"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              style={selectStyle}
            >
              <option value="">— wszystkie —</option>
              <option value={CATEGORY_FILTER_NONE}>— bez kategorii —</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}{cat.active ? '' : ' (wyłączona)'}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div
        style={{
          background: 'var(--surface-card)',
          border: 'var(--border-width) solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <div style={{ padding: 'var(--space-8)', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 'var(--space-3)' }}>
            <style>{'@keyframes clients-spin { to { transform: rotate(360deg); } }'}</style>
            <span
              style={{
                display: 'inline-flex',
                color: 'var(--action-primary)',
                animation: 'clients-spin 0.9s linear infinite',
              }}
            >
              <Icon name="refresh" size={28} />
            </span>
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-body)', color: 'var(--text-secondary)' }}>
              Ładowanie zarządców…
            </span>
          </div>
        ) : clients.length === 0 ? (
          <div style={{ padding: 'var(--space-8)', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-5)' }}>
            <Icon name="users" size={40} color="var(--text-disabled)" />
            <p style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-body)', color: 'var(--text-secondary)' }}>
              Brak zarządców. Dodaj pierwszego, aby zacząć.
            </p>
            <Button variant="secondary" icon="plus" onClick={openAdd}>Dodaj zarządcę</Button>
          </div>
        ) : (
          <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
            {visibleClients.length === 0 && (
              <div style={{ padding: 'var(--space-6)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-body)', color: 'var(--text-secondary)' }}>
                {clientQuery.trim() ? `Brak zarządców dla „${clientQuery.trim()}”.` : 'Brak zarządców w tej kategorii.'}
              </div>
            )}
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={headCellSx}>Nazwa</th>
                  <th style={headCellSx}>Kategoria</th>
                  <th style={headCellSx}>NIP</th>
                  <th style={headCellSx}>Kontakt</th>
                  <th style={headCellRight}>Stawka przeglądu</th>
                  <th style={headCellRight}>Stawka CEEB</th>
                  <th style={headCellCenter}>Budynki</th>
                  <th style={headCellRight}>Akcje</th>
                </tr>
              </thead>
              <tbody>
                {visibleClients.map((c, idx) => {
                  const hasBuildings = (c.buildingsCount ?? 0) > 0;
                  const isLast = idx === visibleClients.length - 1;
                  const cellBorder: React.CSSProperties = isLast ? { borderBottom: 'none' } : {};
                  return (
                    <tr
                      key={c.id}
                      style={{ transition: 'background 120ms ease' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-hover)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = ''; }}
                    >
                      <td style={{ ...bodyCellSx, ...cellBorder }}>
                        <div style={{ fontWeight: 'var(--weight-medium)' as any, color: 'var(--text-primary)' }}>{c.name}</div>
                        {c.shortName && (
                          <div style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>Skrót: {c.shortName}</div>
                        )}
                        {c.address && (
                          <div style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>{c.address}</div>
                        )}
                        {/* 068 (C4): pola wewnętrzne — widoczne tylko w panelu. */}
                        {priceSummary(c) && (
                          <div style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>Cena: {priceSummary(c)}</div>
                        )}
                        {c.internalNote && (
                          <div
                            title={c.internalNote}
                            style={{
                              fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', maxWidth: 320,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}
                          >
                            Notatka: {c.internalNote}
                          </div>
                        )}
                      </td>
                      <td style={{ ...bodyCellSx, ...cellBorder }}>
                        {c.categoryName || <span style={{ color: 'var(--text-disabled)' }}>—</span>}
                      </td>
                      <td style={{ ...bodyCellSx, ...cellBorder }}>
                        {c.nip || <span style={{ color: 'var(--text-disabled)' }}>—</span>}
                      </td>
                      <td style={{ ...bodyCellContact, ...cellBorder }}>
                        {c.phone && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0 }}>
                            <Icon name="phone" size={16} color="var(--text-secondary)" title="Telefon" />
                            <span>{c.phone}</span>
                          </div>
                        )}
                        {c.email && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginTop: c.phone ? 'var(--space-1)' : 0, minWidth: 0 }}>
                            <Icon name="mail" size={16} color="var(--text-secondary)" title="E-mail" />
                            <span style={{ overflowWrap: 'anywhere' }}>{c.email}</span>
                          </div>
                        )}
                        {!c.phone && !c.email && <span style={{ color: 'var(--text-disabled)' }}>—</span>}
                      </td>
                      <td style={{ ...bodyCellRight, ...cellBorder }}>
                        {Number(c.inspectionRate).toFixed(2)} zł
                      </td>
                      <td style={{ ...bodyCellRight, ...cellBorder }}>
                        {Number(c.ceebRate).toFixed(2)} zł
                      </td>
                      <td style={{ ...bodyCellCenter, ...cellBorder }}>
                        {/* Licznik jest KLIKALNY, gdy są budynki — otwiera podgląd + hurtowe
                            przepinanie (spec 2026-08-10). */}
                        <button
                          type="button"
                          onClick={hasBuildings ? () => openBuildingsModal(c) : undefined}
                          disabled={!hasBuildings}
                          aria-label={hasBuildings ? `Pokaż budynki zarządcy ${c.name}` : `Brak budynków zarządcy ${c.name}`}
                          title={hasBuildings ? 'Pokaż budynki / przenieś' : undefined}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            padding: 'var(--space-1) var(--space-3)',
                            borderRadius: 'var(--radius-pill)',
                            fontSize: 'var(--text-caption)',
                            fontWeight: 'var(--weight-medium)' as any,
                            fontFeatureSettings: '"tnum"',
                            fontFamily: 'var(--font-sans)',
                            border: 'none',
                            cursor: hasBuildings ? 'pointer' : 'default',
                            background: hasBuildings ? 'var(--green-100)' : 'var(--gray-100)',
                            color: hasBuildings ? 'var(--green-700)' : 'var(--gray-600)',
                          }}
                        >
                          <Icon name="building" size={16} />
                          {c.buildingsCount ?? 0}
                        </button>
                      </td>
                      <td style={{ ...bodyCellActions, ...cellBorder }}>
                        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
                          <Button
                            variant="ghost"
                            icon="pencil"
                            onClick={() => openEdit(c)}
                            aria-label={`Edytuj zarządcę ${c.name}`}
                            style={{ minHeight: 'var(--touch-min)', padding: '0 var(--space-3)', flexShrink: 0 }}
                          >
                            Edytuj
                          </Button>
                          <Button
                            variant="ghost"
                            icon="trash"
                            onClick={() => openDeleteConfirm(c)}
                            aria-label={`Usuń zarządcę ${c.name}`}
                            style={{ minHeight: 'var(--touch-min)', padding: '0 var(--space-3)', color: 'var(--status-deny)', flexShrink: 0 }}
                          >
                            Usuń
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ModalShell
        open={dialogOpen}
        title={editingId ? 'Edytuj zarządcę' : 'Dodaj nowego zarządcę'}
        onClose={() => { if (!saving) setDialogOpen(false); }}
        width={560}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDialogOpen(false)} disabled={saving}>Anuluj</Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Zapisywanie…' : (editingId ? 'Zapisz zmiany' : 'Dodaj zarządcę')}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
          <InputField
            label="Nazwa firmy / wspólnoty"
            value={form.name}
            onChange={e => { setForm({ ...form, name: e.target.value }); if (nameError) setNameError(null); }}
            required
            autoFocus
            errorMessage={nameError ?? undefined}
          />
          <InputField
            label="Skrócona nazwa (kolumna „Zarządca” w Budynkach)"
            placeholder="np. Complex, Avente, ZBM"
            value={form.shortName}
            maxLength={60}
            onChange={e => setForm({ ...form, shortName: e.target.value })}
            hint="Puste = w Budynkach pokaże się pełna nazwa."
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <label htmlFor="client-category" style={selectLabelStyle}>Kategoria</label>
            <select
              id="client-category"
              value={form.categoryId}
              onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
              style={selectStyle}
            >
              <option value="">— bez kategorii —</option>
              {formCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}{cat.active ? '' : ' (wyłączona)'}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'row', gap: 'var(--space-3)' }}>
            <div style={{ flex: 1 }}>
              <InputField
                label="NIP"
                value={form.nip}
                onChange={e => { setForm({ ...form, nip: e.target.value }); if (nipError) setNipError(null); }}
                errorMessage={nipError ?? undefined}
              />
            </div>
            <div style={{ flex: 1 }}>
              <InputField label="Telefon" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
            </div>
          </div>
          <InputField label="Adres" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
          <InputField
            label="E-mail"
            type="email"
            value={form.email}
            onChange={e => { setForm({ ...form, email: e.target.value }); if (emailError) setEmailError(null); }}
            errorMessage={emailError ?? undefined}
          />
          <div style={{ display: 'flex', flexDirection: 'row', gap: 'var(--space-3)', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 'var(--space-2)' }}>
              <InputField
                label="Stawka za przegląd lokalu"
                type="number"
                value={form.inspectionRate}
                onChange={e => setForm({ ...form, inspectionRate: e.target.value })}
              />
              <span style={{ paddingBottom: 'var(--space-3)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-body)', color: 'var(--text-secondary)' }}>zł</span>
            </div>
            <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 'var(--space-2)' }}>
              <InputField
                label="Stawka za wpis CEEB"
                type="number"
                value={form.ceebRate}
                onChange={e => setForm({ ...form, ceebRate: e.target.value })}
              />
              <span style={{ paddingBottom: 'var(--space-3)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-body)', color: 'var(--text-secondary)' }}>zł</span>
            </div>
          </div>

          {/* 068 (C4, Bronek 12.10): pola WEWNĘTRZNE — nie trafiają do protokołu, PDF, CEEB ani maila. */}
          <fieldset
            style={{
              margin: 0, padding: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)',
              border: 'var(--border-width) dashed var(--border-strong)', borderRadius: 'var(--radius-md)',
            }}
          >
            <legend style={{ ...selectLabelStyle, padding: '0 var(--space-2)' }}>
              Informacje wewnętrzne — nie trafiają do protokołu ani dokumentów
            </legend>
            <InputField
              label="Notatka wewnętrzna"
              multiline
              rows={3}
              maxLength={5000}
              placeholder="z kim kontakt, ustalenia"
              value={form.internalNote}
              onChange={e => setForm({ ...form, internalNote: e.target.value })}
            />
            <div style={{ display: 'flex', flexDirection: 'row', gap: 'var(--space-3)', alignItems: 'flex-start' }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                <label htmlFor="client-price-mode" style={selectLabelStyle}>Cena</label>
                <select
                  id="client-price-mode"
                  value={form.priceMode}
                  onChange={(e) => setForm({ ...form, priceMode: e.target.value as FormState['priceMode'] })}
                  style={selectStyle}
                >
                  <option value="">— nie ustalono —</option>
                  <option value="per_apartment">Ryczałt za lokal (stawka za przegląd lokalu)</option>
                  <option value="flat">Jedna cena za całość</option>
                </select>
              </div>
              {form.priceMode === 'flat' && (
                <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 'var(--space-2)' }}>
                  <InputField
                    label="Cena za całość"
                    inputMode="decimal"
                    value={form.priceFlat}
                    onChange={e => { setForm({ ...form, priceFlat: e.target.value }); if (priceFlatError) setPriceFlatError(null); }}
                    errorMessage={priceFlatError ?? undefined}
                  />
                  <span style={{ paddingBottom: 'var(--space-3)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-body)', color: 'var(--text-secondary)' }}>zł</span>
                </div>
              )}
            </div>
          </fieldset>
        </div>
      </ModalShell>

      <ClientCategoriesModal
        open={categoriesOpen}
        categories={categories}
        onClose={() => setCategoriesOpen(false)}
        onChanged={async () => { await loadCategories(); await loadClients(); }}
      />

      {/* Modal „Budynki zarządcy" — podgląd + hurtowe przepinanie (spec 2026-08-10). */}
      <ModalShell
        open={!!buildingsClient}
        title={buildingsClient ? `Budynki zarządcy: ${buildingsClient.name}` : ''}
        width={640}
        onClose={() => setBuildingsClient(null)}
        footer={
          <>
            {clientBuildings.length > 0 && (
              <Button
                variant="ghost"
                icon="flame"
                data-testid="client-heat-source-summary"
                onClick={() => { setHsSummaryClient(buildingsClient); setBuildingsClient(null); }}
              >
                Podsumowanie źródeł ciepła
              </Button>
            )}
            <Button variant="secondary" onClick={() => setBuildingsClient(null)}>Zamknij</Button>
            <Button
              variant="primary"
              disabled={moving || selectedIds.size === 0}
              onClick={handleMoveBuildings}
            >
              {moveTarget ? `Przenieś (${selectedIds.size})` : `Odepnij (${selectedIds.size})`}
            </Button>
          </>
        }
      >
        {buildingsLoading ? (
          <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-body)' }}>Ładowanie…</div>
        ) : (
          <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <label
                htmlFor="move-target-client"
                style={{
                  fontFamily: 'var(--font-sans)', fontSize: 'var(--text-label)',
                  fontWeight: 'var(--weight-semibold)' as any, color: 'var(--text-secondary)',
                }}
              >
                Przenieś zaznaczone do
              </label>
              <select
                id="move-target-client"
                value={moveTarget}
                disabled={moving}
                onChange={(e) => setMoveTarget(e.target.value)}
                style={{
                  width: '100%', minHeight: 'var(--touch-min)', padding: '0 var(--space-4)',
                  boxSizing: 'border-box', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-body)',
                  color: 'var(--text-primary)', background: 'var(--surface-card)',
                  border: 'var(--border-width-strong) solid var(--border-strong)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                <option value="">— bez zarządcy (odepnij) —</option>
                {clients.filter((c) => c.id !== buildingsClient?.id).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>
              <input
                type="checkbox"
                checked={clientBuildings.length > 0 && selectedIds.size === clientBuildings.length}
                onChange={(e) => setSelectedIds(e.target.checked ? new Set(clientBuildings.map((b) => b.id)) : new Set())}
              />
              Zaznacz wszystkie ({clientBuildings.length})
            </label>
            <div style={{ maxHeight: 360, overflowY: 'auto', display: 'grid', gap: 'var(--space-1)' }}>
              {clientBuildings.map((b) => (
                <label
                  key={b.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
                    padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-md)',
                    fontFamily: 'var(--font-sans)', fontSize: 'var(--text-body)', color: 'var(--text-primary)',
                    background: selectedIds.has(b.id) ? 'var(--surface-hover)' : 'transparent',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(b.id)}
                    onChange={(e) => {
                      const next = new Set(selectedIds);
                      if (e.target.checked) next.add(b.id); else next.delete(b.id);
                      setSelectedIds(next);
                    }}
                  />
                  {b.street} {b.houseNumber}, {b.city}
                </label>
              ))}
              {clientBuildings.length === 0 && (
                <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-body)' }}>
                  Brak budynków przypisanych do tego zarządcy.
                </div>
              )}
            </div>

            {/* Hurtowe przypisywanie po wyszukiwarce (właściciel 2026-09-15). */}
            <div style={{ borderTop: 'var(--border-width) solid var(--border)', paddingTop: 'var(--space-4)', display: 'grid', gap: 'var(--space-3)' }}>
              <h3 style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-body)', fontWeight: 'var(--weight-semibold)' as any, color: 'var(--text-heading)' }}>
                Dodaj budynki do zarządcy
              </h3>
              <InputField
                label="Szukaj adresu"
                placeholder="np. Harcerska 17"
                value={attachQuery}
                disabled={attaching}
                onChange={(e) => setAttachQuery(e.target.value)}
                hint={attachSearching ? 'Szukam…' : 'Wpisz ulicę lub ulicę i numer. Budynek innego zarządcy też można zaznaczyć — przepięcie trzeba potwierdzić.'}
              />
              {attachHits.length > 0 && (
                <div role="group" aria-label="Wyniki wyszukiwania" style={{ maxHeight: 240, overflowY: 'auto', display: 'grid', gap: 'var(--space-1)' }}>
                  {attachHits.map((h) => {
                    const own = h.clientId === buildingsClient?.id;
                    const foreign = !!h.clientId && !own;
                    // Cudze trafienie da się zaznaczyć — przepięcie potwierdza ConfirmModal.
                    const disabled = own || attaching;
                    return (
                      <label
                        key={h.id}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
                          padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-md)',
                          fontFamily: 'var(--font-sans)', fontSize: 'var(--text-body)',
                          color: disabled ? 'var(--text-muted)' : 'var(--text-primary)',
                          background: attachIds.has(h.id) ? 'var(--surface-hover)' : 'transparent',
                        }}
                      >
                        <input
                          type="checkbox"
                          disabled={disabled}
                          checked={attachIds.has(h.id)}
                          onChange={(e) => {
                            const next = new Set(attachIds);
                            if (e.target.checked) next.add(h.id); else next.delete(h.id);
                            setAttachIds(next);
                          }}
                        />
                        <span>{h.street} {h.houseNumber}, {h.city}</span>
                        {own && <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>już przypisany</span>}
                        {foreign && <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>zarządca: {h.clientName}</span>}
                      </label>
                    );
                  })}
                </div>
              )}
              {attachQuery.trim().length >= 2 && !attachSearching && attachHits.length === 0 && (
                <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-body)' }}>Brak budynków dla „{attachQuery.trim()}”.</div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="primary" disabled={attaching || attachIds.size === 0} onClick={requestAttachBuildings}>
                  {attaching ? 'Przypisywanie…' : `Przypisz (${attachIds.size})`}
                </Button>
              </div>
            </div>
          </div>
        )}
      </ModalShell>

      <ConfirmModal
        open={!!reassignConfirm}
        title="Zmienić zarządcę budynków?"
        message={reassignConfirm ? (
          <div>
            <p style={{ margin: 0 }}>
              Te budynki mają już innego zarządcę — zostaną przepięte do „{buildingsClient?.name}":
            </p>
            <ul style={{ margin: 'var(--space-2) 0', paddingLeft: 'var(--space-5)' }}>
              {reassignConfirm.map((h) => (
                <li key={h.id}>{h.street} {h.houseNumber}, {h.city} — teraz: {h.clientName || '—'}</li>
              ))}
            </ul>
          </div>
        ) : ''}
        confirmLabel="Zmień zarządcę"
        cancelLabel="Anuluj"
        onConfirm={() => { setReassignConfirm(null); void handleAttachBuildings(true); }}
        onCancel={() => setReassignConfirm(null)}
      />

      <ConfirmModal
        open={!!confirmTarget}
        title="Usunąć zarządcę?"
        message={confirmTarget ? (
          (confirmTarget.buildingsCount ?? 0) > 0 ? (
            <div>
              <p style={{ margin: 0 }}>
                Zarządca „{confirmTarget.name}" ma podpięte budynki: <strong>{confirmTarget.buildingsCount}</strong>.
              </p>
              {confirmSample.length > 0 && (
                <ul style={{ margin: 'var(--space-2) 0', paddingLeft: 'var(--space-5)' }}>
                  {confirmSample.map((a) => <li key={a}>{a}</li>)}
                  {(confirmTarget.buildingsCount ?? 0) > confirmSample.length && (
                    <li>… i {(confirmTarget.buildingsCount ?? 0) - confirmSample.length} więcej</li>
                  )}
                </ul>
              )}
              <p style={{ margin: 0 }}>
                Usunięcie <strong>odepnie</strong> te budynki od zarządcy — budynki zostają w systemie. Usunąć?
              </p>
            </div>
          ) : `Usunąć zarządcę "${confirmTarget.name}"? Tej operacji nie można cofnąć.`
        ) : ''}
        confirmLabel="Usuń"
        cancelLabel="Anuluj"
        destructive
        onConfirm={() => {
          const target = confirmTarget;
          setConfirmTarget(null);
          if (target) handleDelete(target);
        }}
        onCancel={() => setConfirmTarget(null)}
      />

      {/* Toast (DS), zachowuje rozróżnienie success/error */}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: 'var(--space-6)',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1400,
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            maxWidth: 'min(92vw, 560px)',
            padding: 'var(--space-3) var(--space-4)',
            background: toast.severity === 'success' ? 'var(--status-ok-bg)' : 'var(--status-deny-bg)',
            border: 'var(--border-width) solid var(--border-strong)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-float)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-label)',
            color: toast.severity === 'success' ? 'var(--status-ok)' : 'var(--status-deny)',
          }}
        >
          <Icon
            name={toast.severity === 'success' ? 'check' : 'triangle-alert'}
            size={18}
            color={toast.severity === 'success' ? 'var(--status-ok)' : 'var(--status-deny)'}
          />
          <span style={{ flex: 1 }}>{toast.msg}</span>
          <button
            type="button"
            aria-label="Zamknij powiadomienie"
            onClick={() => setToast(null)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              flex: 'none',
              padding: 0,
              background: 'transparent',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              color: 'inherit',
              cursor: 'pointer',
            }}
          >
            <Icon name="x" size={18} />
          </button>
        </div>
      )}

      <HeatSourceSummaryModal
        selection={hsSummaryClient ? { clientId: hsSummaryClient.id } : null}
        subject={hsSummaryClient?.name}
        onClose={() => setHsSummaryClient(null)}
      />
    </div>
  );
};

export default ClientsTab;
